"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * Karta 6 — „Grafik, prowizje, urlopy"
 * Mini-kalendarz tygodniowy z 5 lekarzami × 5 godzin
 * Wpisy wizyt pojawiają się sekwencyjnie, kolory = różne typy wizyt (różne prowizje)
 * Jedna kolumna = blok URLOP (lekarz nie pracuje)
 */

const DOCTORS = [
  { initials: "AK", color: "#3B82F6" }, // niebieski
  { initials: "MN", color: "#22C55E" }, // zielony
  { initials: "PT", color: "#F59E0B" }, // pomarańczowy
  { initials: "JS", color: "#A855F7" }, // fioletowy (URLOP)
  { initials: "KM", color: "#EC4899" }, // pink
];

type EntryType = "consultation" | "vaccine" | "procedure" | "home";

const ENTRY_STYLES: Record<EntryType, { bg: string; text: string }> = {
  consultation: { bg: "#DBEAFE", text: "#1E40AF" },
  vaccine: { bg: "#D1FAE5", text: "#065F46" },
  procedure: { bg: "#EDE9FE", text: "#5B21B6" },
  home: { bg: "#FED7AA", text: "#9A3412" },
};

// Stawki prowizji per typ wizyty (PLN)
const COMMISSION: Record<EntryType, number> = {
  consultation: 25,
  vaccine: 15,
  procedure: 60,
  home: 80,
};

// 2 lekarzy do śledzenia w sekcji „Prowizje dziś"
// AK (col 0) — 4 wizyty, mix typów = 125 zł
// KM (col 4) — 3 wizyty z home visit = 130 zł
const TRACKED_DOCS = [
  { col: 0, initials: "AK", name: "Anna K.", color: "#3B82F6" },
  { col: 4, initials: "KM", name: "Kamila M.", color: "#EC4899" },
];

const COMMISSION_MAX = 200; // referencja dla progress bar (100%)

// Grid: 5 cols × 5 rows. Każdy entry: { col, row, type, name }
// Col 3 (index 3, JS) = URLOP
const ENTRIES: { col: number; row: number; type: EntryType; name: string }[] = [
  { col: 0, row: 0, type: "consultation", name: "Reksio" },
  { col: 1, row: 0, type: "vaccine", name: "Mruczek" },
  { col: 2, row: 0, type: "consultation", name: "Filemon" },
  { col: 4, row: 0, type: "home", name: "Bella" },
  { col: 0, row: 1, type: "procedure", name: "Burek" },
  { col: 1, row: 1, type: "consultation", name: "Maja" },
  { col: 2, row: 1, type: "vaccine", name: "Tofik" },
  { col: 4, row: 1, type: "consultation", name: "Cyryl" },
  { col: 1, row: 2, type: "consultation", name: "Lola" },
  { col: 2, row: 2, type: "procedure", name: "Azor" },
  { col: 0, row: 2, type: "vaccine", name: "Kicia" },
  { col: 4, row: 2, type: "consultation", name: "Puma" },
  { col: 0, row: 3, type: "consultation", name: "Tytus" },
  { col: 1, row: 3, type: "vaccine", name: "Kropka" },
  { col: 2, row: 3, type: "consultation", name: "Bono" },
];

export function VisualTeam() {
  // 0 = pusty grid + URLOP, 1-15 = wpisy się pojawiają, 16 = idle, 17 = reset
  const [step, setStep] = useState(0);
  const TOTAL_STEPS = ENTRIES.length + 3; // 15 entries + 3 buffer steps (idle + reset)

  useEffect(() => {
    const isIdle = step === ENTRIES.length + 1;
    const isReset = step === ENTRIES.length + 2;
    const delay = isIdle ? 2000 : isReset ? 1500 : 200;

    const t = setTimeout(() => {
      setStep((s) => (s + 1) % TOTAL_STEPS);
    }, delay);
    return () => clearTimeout(t);
  }, [step, TOTAL_STEPS]);

  // Wpisy widoczne: te, których indeks < step (clamp do liczby entries)
  const visibleEntriesCount = step >= ENTRIES.length + 2 ? 0 : Math.min(step, ENTRIES.length);

  // Per-doctor stats z widocznych entries
  const visibleEntries = ENTRIES.slice(0, visibleEntriesCount);
  const docStats = TRACKED_DOCS.map((doc) => {
    const docEntries = visibleEntries.filter((e) => e.col === doc.col);
    const commission = docEntries.reduce(
      (sum, e) => sum + COMMISSION[e.type],
      0,
    );
    return {
      ...doc,
      visits: docEntries.length,
      commission,
      progress: Math.min(commission / COMMISSION_MAX, 1),
    };
  });

  return (
    <div
      className="absolute inset-x-4 bottom-4 top-2 pointer-events-none"
      aria-hidden
    >
      <div className="w-full h-full rounded-xl bg-card border border-border/60 shadow-sm overflow-hidden flex flex-col">
        {/* Header row: doctor avatars */}
        <div className="grid grid-cols-[28px_repeat(5,1fr)] gap-[3px] px-2 pt-2 pb-1.5">
          <div /> {/* Empty cell above time column */}
          {DOCTORS.map((d) => (
            <div
              key={d.initials}
              className="flex items-center justify-center h-[20px]"
            >
              <div
                className="h-4 w-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                style={{ background: d.color }}
              >
                {d.initials}
              </div>
            </div>
          ))}
        </div>

        {/* Grid: time column + 5×4 cells */}
        <div className="px-2 pb-2 grid grid-cols-[28px_repeat(5,1fr)] gap-[3px]">
          {[0, 1, 2, 3].map((row) => (
            <div key={`row-${row}`} className="contents">
              {/* Time label */}
              <div className="text-[8px] text-muted-foreground tabular-nums flex items-center pr-1">
                {`${9 + row}:00`}
              </div>
              {/* 5 cells */}
              {[0, 1, 2, 3, 4].map((col) => {
                // Column 3 (JS) = URLOP block
                if (col === 3) {
                  // Render URLOP only on first row (it spans all rows visually)
                  if (row === 0) {
                    return (
                      <div
                        key={`cell-${col}-${row}`}
                        className="row-span-4 rounded-md flex items-center justify-center relative overflow-hidden"
                        style={{
                          gridRow: "span 4",
                          background:
                            "repeating-linear-gradient(45deg, #F1F2F6 0px, #F1F2F6 4px, #E8EAF0 4px, #E8EAF0 8px)",
                        }}
                      >
                        <span
                          className="text-[8px] font-bold tracking-widest text-muted-foreground"
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                          }}
                        >
                          URLOP
                        </span>
                      </div>
                    );
                  }
                  // Other rows of URLOP column — empty (covered by row-span)
                  return null;
                }

                // Find entry for this cell
                const entryIndex = ENTRIES.findIndex(
                  (e) => e.col === col && e.row === row,
                );
                const entry = entryIndex !== -1 ? ENTRIES[entryIndex] : null;
                const isVisible =
                  entry !== null && entryIndex < visibleEntriesCount;

                return (
                  <div
                    key={`cell-${col}-${row}`}
                    className="rounded-md min-h-[24px] flex items-center justify-center relative"
                    style={{
                      background: isVisible && entry
                        ? ENTRY_STYLES[entry.type].bg
                        : "#F8F9FB",
                    }}
                  >
                    {isVisible && entry && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          duration: 0.3,
                          ease: [0.21, 0.47, 0.32, 0.98],
                        }}
                        className="text-[8px] font-medium px-1 truncate"
                        style={{ color: ENTRY_STYLES[entry.type].text }}
                      >
                        {entry.name}
                      </motion.span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Prowizje dziś — animowane podsumowanie 2 lekarzy */}
        <div className="mt-auto px-2.5 pb-2.5 pt-2 border-t border-border/40 bg-muted/20">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[7px] font-semibold uppercase tracking-wider text-muted-foreground">
              Prowizje dziś
            </span>
            <span className="text-[7px] text-muted-foreground tabular-nums">
              naliczane automatycznie
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            {docStats.map((doc) => (
              <div key={doc.initials} className="flex items-center gap-2">
                <div
                  className="h-4 w-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white flex-shrink-0"
                  style={{ background: doc.color }}
                >
                  {doc.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-[8px] font-semibold text-foreground">
                      {doc.name}
                    </span>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[7px] text-muted-foreground tabular-nums">
                        {doc.visits} {doc.visits === 1 ? "wizyta" : "wizyt"}
                      </span>
                      <motion.span
                        key={`${doc.initials}-${doc.commission}`}
                        initial={{ opacity: 0.4, y: -2 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="text-[10px] font-bold tabular-nums"
                        style={{ color: doc.color }}
                      >
                        {doc.commission} zł
                      </motion.span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-[3px] w-full bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      initial={false}
                      animate={{ width: `${doc.progress * 100}%` }}
                      transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
                      style={{ background: doc.color }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
